import React, { useEffect, useMemo, useState } from "react";
import MetaTags from "react-meta-tags";
import jwt_decode from "jwt-decode";
import moment from "moment";
import axios from "../api/axios";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import ManagementReports from "../ManagerReports/ManagementReports";
import FilterBar from "../../components/dashboard/FilterBar";
import DashboardCharts from "./sections/DashboardCharts";
import DashboardKpis from "./sections/DashboardKpis";
import "../../components/dashboard/dashboard.scss";

const COMPANY_DASHBOARD_URL = "/dashboard/company_stat";
const MANAGER_DASHBOARD_URL = "/dashboard/manager_stat";
const GET_MANAGERS = "/company/get_managers";

const safeDecode = () => {
  try {
    return jwt_decode(window.localStorage.getItem("accessToken"));
  } catch (e) {
    return {};
  }
};

const Dashboard = () => {
  const role = window.localStorage.getItem("role");

  const decode = useMemo(safeDecode, []);

  const initialManagerID = useMemo(() => {
    if (role !== "manager") return { label: "", value: "" };
    try {
      const stored = JSON.parse(window.localStorage.getItem("authUser"));
      return { label: "", value: stored && stored.userData && stored.userData.id };
    } catch (e) {
      return { label: "", value: "" };
    }
  }, [role]);

  const [managerID, setManagerID] = useState(initialManagerID);
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyList, setPropertyList] = useState([]);
  const [companyManagers, setCompanyManagers] = useState([]);
  const [bootLoading, setBootLoading] = useState(false);

  const loadDashboardScaffolding = async (url, body) => {
    setBootLoading(true);
    try {
      const response = await axios.post(url, body);
      if (response.data && response.data.success) {
        setPropertyList(response.data.propertyList || []);
      }
    } catch (e) {
      // boot data is non-critical for charts; swallow
    } finally {
      setBootLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      const response = await axios.post(GET_MANAGERS, { company_id: decode.id });
      if (response.data && response.data.status === 200) {
        const list = (response.data.managers || []).map((m) => ({
          label: `${m.firstname || ""} ${m.lastname || ""}`.trim(),
          value: m._id,
        }));
        setCompanyManagers(list);
      }
    } catch (e) {
      // non-critical
    }
  };

  useEffect(() => {
    if (role === "company" || role === "admin") {
      loadDashboardScaffolding(COMPANY_DASHBOARD_URL, {
        companyId: decode.id,
        companyDomain: decode.domain,
      });
      loadManagers();
    } else if (role === "manager") {
      loadDashboardScaffolding(MANAGER_DASHBOARD_URL, {
        managerId: decode.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartDateChange = (value) => {
    setSelectedStartDate(value);
    if (value && !selectedEndDate) {
      setSelectedEndDate(moment().format("YYYY-MM-DD"));
    }
  };

  const handleClear = () => {
    if (role !== "manager") {
      setManagerID({ label: "", value: "" });
    }
    setSelectedProperty(null);
    setSelectedStartDate("");
    setSelectedEndDate("");
  };

  const canClear =
    (role !== "manager" && !!managerID.value) ||
    !!(selectedProperty && selectedProperty.value) ||
    !!selectedStartDate ||
    !!selectedEndDate;

  const filters = useMemo(
    () => ({
      selectedStartDate,
      selectedEndDate,
      propertyManagerID: managerID.value,
      selectedProperty,
    }),
    [selectedStartDate, selectedEndDate, managerID, selectedProperty]
  );

  if (!(role === "admin" || role === "company" || role === "manager")) {
    if (role === "customer") window.location.replace("/tickets_list");
    else if (role === "vendor") window.location.replace("/working-tickets");
    else if (role === "technical staff")
      window.location.replace("/technical_staff_tickets");
    else if (role === "rm vendor")
      window.location.replace("/inventory-orders-management");
    else if (role === "auditor") window.location.replace("/auditor-soa");
    return null;
  }

  return (
    <React.Fragment>
      <div className="page-content rdc-page">
        <MetaTags>
          <title>RentDigiCare | Dashboard</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs title="Home" breadcrumbItem="Dashboard" />

          <header className="rdc-header">
            <div className="rdc-header__titles">
              <span className="rdc-header__eyebrow">Overview</span>
              <h1 className="rdc-header__title">Dashboard</h1>
              <p className="rdc-header__subtitle">
                Live performance across applications, maintenance and properties.
              </p>
            </div>
          </header>

          <FilterBar
            showManager={decode.role === "company"}
            showManagerChip={role !== "manager"}
            managers={companyManagers}
            managerValue={managerID}
            onManagerChange={setManagerID}
            properties={propertyList}
            propertyValue={selectedProperty}
            onPropertyChange={setSelectedProperty}
            startDate={selectedStartDate}
            onStartDateChange={handleStartDateChange}
            endDate={selectedEndDate}
            onEndDateChange={setSelectedEndDate}
            onClear={handleClear}
            canClear={canClear}
          />

          {!bootLoading && (
            <DashboardKpis decode={decode} filters={filters} />
          )}

          <DashboardCharts
            decode={decode}
            managerID={managerID}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            selectedProperty={selectedProperty}
          />

          {decode.role === "company" && (
            <section className="rdc-section" aria-label="Operational reports">
              <header className="rdc-section__head">
                <div className="rdc-section__titles">
                  <span className="rdc-section__eyebrow">Operations</span>
                  <h2 className="rdc-section__title">Detailed reports</h2>
                  <p className="rdc-section__subtitle">
                    Appointments, maintenance, vendors and resident applications.
                  </p>
                </div>
              </header>
              <div className="rdc-section__body rdc-reports">
                <ManagementReports />
              </div>
            </section>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
